import { AxiosPromise, AxiosRequestConfig } from 'axios';
import * as Moment from 'moment';
import { generateGuid } from '../../util/stringHelper';
import axios, { axiosInstance, retryAxiosInstance } from './axios';
import loader from './loader';

import WebStorageService from '../webStorage';
import Auth from '../../util/auth/auth';
import config from '../../configs/config';
import auth from '../../util/auth/auth';

type PossibleHttpMethods = 'get' | 'post' | 'patch' | 'put' | 'delete';

export type UpdatePayloadType<T> = {
	[K in keyof T]: { Value: T[K] };
};

const getRepeatedDate = () => Moment().format('YYYYMMDDYYYYMMDDYYYYMMDDYYYYMMDD'); // date like this so that the blob service does not throw a validation error for the userid format

export const getApiKey = (): string | undefined => {
	if (auth.getAuthData()) {
		return auth.getAuthData()?.info.ApiKey;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_user');
		return user && JSON.parse(user).ApiKey;
	}
};

export const getUserEmail = (): string | undefined => {
	if (auth.getAuthData()) {
		return auth.getAuthData()?.info.Email;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_user');
		return user && JSON.parse(user).Email;
	}
};

export const getParentApiKey = (): string | undefined => {
	if (auth.getParentData()) {
		return auth.getParentData()?.info.ApiKey;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_parent');
		return user && JSON.parse(user).ApiKey;
	}
};

export const getParentEmail = (): string | undefined => {
	if (auth.getParentData()) {
		return auth.getParentData()?.info.Email;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_parent');
		return user && JSON.parse(user).Email;
	}
};

export const getParentTeamMemberId = (): string | undefined => {
	if (auth.getParentData()) {
		return auth.getParentData()?.info.TeamMemberId;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_parent');
		return user && JSON.parse(user).TeamMemberId;
	}
};

export const getUserEmailFromLsForExport = (): string | undefined => {
	const parentUser = auth.getParentData();
	const user = getUser();
	if (parentUser) {
		return parentUser.info.TeamMemberEmail;
	} else if (user) {
		return user.TeamMemberEmail;
	}
};

export const getTeamMemberId = (): string | undefined => {
	if (auth.getAuthData()) {
		return auth.getAuthData()?.info.TeamMemberId;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_user');
		return user && JSON.parse(user).TeamMemberId;
	}
};

export const getTeamMemberEmail = (): string | undefined => {
	if (auth.getAuthData()) {
		return auth.getAuthData()?.info.TeamMemberEmail;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_user');
		return user && JSON.parse(user).TeamMemberEmail;
	}
};

export const getUserId = (): string | undefined => {
	if (auth.getAuthData()) {
		return auth.getAuthData()?.info.UserId;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_user');
		return user && JSON.parse(user).UserId;
	}
};

export const getUser = () => {
	if (auth.getAuthData()) {
		return auth.getAuthData()?.info;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_user');
		return user && JSON.parse(user);
	}
};

export const getMemberName = (): string | undefined => {
	if (auth.getAuthData()) {
		return auth.getAuthData()?.info.Name;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_user');
		return user && JSON.parse(user).Name;
	}
};

export const isClient = (): boolean | undefined => {
	if (auth.getAuthData()) {
		return auth.getAuthData().info.IsSendAccount ? false : auth.getAuthData()?.info.IsClient;
	} else {
		const user = WebStorageService.LocalStorage.get('moo_user');
		return user && JSON.parse(user).IsClient;
	}
};

const makeApiRetryCall = <T = any, S = object>(
	url: string,
	method: 'get' | 'post' | 'patch' | 'put' | 'delete',
	payload?: S,
	config?: AxiosRequestConfig,
	withNoLoader?: boolean
): AxiosPromise<T> => {
	let promise: AxiosPromise<T>;
	const ax = retryAxiosInstance; // this is in order to avoid our pre set axios config and use a fresh default instance

	const takeAction = () => {
		switch (method.toLowerCase()) {
			case 'get':
				promise = ax.get<T>(url, {
					...config,
					'axios-retry': {
						retries: 3,
						retryDelay: () => 1000,
						retryCondition: (error) => error?.response?.status === 404,
					},
				});
				break;
			case 'post':
				promise = ax.post<T>(url, payload, config);
				break;
			case 'put':
				promise = ax.put<T>(url, payload, config);
				break;
			case 'delete':
				promise = ax.delete(url, config);
				break;
			case 'patch':
				promise = ax.patch<T>(url, payload, config);
				break;
			default:
				promise = Promise.reject('Method not supported');
		}

		return promise;
	};

	if (withNoLoader) promise = takeAction();
	else {
		loader.loaderRenderer.start();

		promise = takeAction();

		promise.then(() => {
			loader.loaderRenderer.complete();
		});
	}

	return promise;
};

export const makeApiRetryGatewayCall = <T = any, S = object>(
	url: string,
	method: 'get' | 'post' | 'patch' | 'put' | 'delete',
	payload?: S,
	extraHeaders?: { [key: string]: string },
	requestConfig?: Pick<AxiosRequestConfig, Exclude<keyof AxiosRequestConfig, 'headers'>>,
	useDefaultAxios: boolean = true,
	withNoLoader?: boolean
): AxiosPromise<T> => {
	const apikey = getApiKey();
	const teamMemberId = getTeamMemberId();
	if (!apikey) {
		!location.href.includes('localhost') && (location.href = '/login');
		throw new Error('key is not present!!!');
	}

	const conf = {
		headers: {
			'X-ApiKey': apikey,
			// 'X-ApiKey': url.includes('shapeshift') ? 'asdasd' : apikey,
			'X-TeamMemberId': teamMemberId,
			...extraHeaders,
		},
		...requestConfig,
	};

	return makeApiRetryCall<T, S>(url, method, payload, conf, withNoLoader);
};

const makeApiCall = <T = any, S = object>(
	url: string,
	method: PossibleHttpMethods,
	payload?: S,
	config?: AxiosRequestConfig,
	useDefaultAxios: boolean = true,
	withNoLoader?: boolean
): AxiosPromise<T> => {
	let promise: AxiosPromise<T>;
	const ax = useDefaultAxios ? axios : axiosInstance; // this is in order to avoid our pre set axios config and use a fresh default instance

	const takeAction = () => {
		switch (method.toLowerCase()) {
			case 'get':
				promise = ax.get<T>(url, config);
				break;
			case 'post':
				promise = ax.post<T>(url, payload, config);
				break;
			case 'put':
				promise = ax.put<T>(url, payload, config);
				break;
			case 'delete':
				promise = ax.delete(url, config);
				break;
			case 'patch':
				promise = ax.patch<T>(url, payload, config);
				break;
			default:
				promise = Promise.reject('Method not supported');
		}

		return promise;
	};

	if (withNoLoader) promise = takeAction();
	else {
		loader.loaderRenderer.start();

		promise = takeAction();

		promise.then(() => {
			loader.loaderRenderer.complete();
		});
	}

	return promise;
};

/**
 * helper function to call moosend apis via gateway, the generic T parameter is for the expected response data type
 *
 * @param url
 * @param method
 * @param payload
 * @param extraHeaders
 */
export const makeApiGatewayCall = async <T = any, S = object>(
	url: string,
	method: PossibleHttpMethods,
	payload?: S,
	extraHeaders?: { [key: string]: string },
	requestConfig?: Pick<AxiosRequestConfig, Exclude<keyof AxiosRequestConfig, 'headers'>>,
	useDefaultAxios: boolean = true,
	withNoLoader?: boolean
): AxiosPromise<T> => {
	try {
		const apikey = getApiKey();
		const teamMemberId = getTeamMemberId();

		const isIdToken = url.includes(config.auth.impersonation) ? true : false;

		const token = await Auth.getToken(isIdToken);

		const conf = !Auth.getIsOldLogin()
			? {
					headers: {
						Authorization: `Bearer ${token}`,
						...extraHeaders,
					},
					...requestConfig,
			  }
			: {
					headers: {
						'X-ApiKey': apikey,
						'X-TeamMemberId': teamMemberId,
						...extraHeaders,
					},
					...requestConfig,
			  };

		const rsp = await makeApiCall<T, S>(url, method, payload, conf, useDefaultAxios, withNoLoader);

		return rsp;
	} catch (e) {
		// handle if token is expired
		if (e?.response?.status === 401) {
			Auth.redirectToLogin();
		} else {
			throw e;
		}
	}
};

function _ajax(params: any) {
	if (!params) return;
	if (!params.type) params.type = 'GET';

	var url = params.url;

	// prevent IE cache
	if (url.indexOf('?') == -1) url = url + '?';
	else url = url + '&';
	url = url + '_t=' + new Date().getTime();

	var xmlhttp = new XMLHttpRequest();
	//else xmlhttp = new ActiveXObject("Microsoft.XMLHTTP");

	xmlhttp.onreadystatechange = function () {
		if (xmlhttp.readyState == 4 && xmlhttp.status == 200) {
			if (params.success) {
				params.success(xmlhttp.responseText);
			}
		} else if (xmlhttp.readyState == 4) {
			if (params.error) params.error(xmlhttp, xmlhttp.status, xmlhttp.responseText);
		}
	};

	xmlhttp.open(params.type, url, true);
	//xmlhttp.setRequestHeader('User-Agent','Moosend Javascript Api');

	xmlhttp.setRequestHeader('Accept', 'application/json, text/javascript, */*; q=0.01');

	if (params.data) {
		xmlhttp.setRequestHeader('Content-Type', 'application/json');
		xmlhttp.send(JSON.stringify(params.data));
	} else xmlhttp.send();

	return xmlhttp;
}

/**
 * helper function to call moosend apis via gateway, the generic T parameter is for the expected response data type
 *
 * @param url
 * @param method
 * @param payload
 * @param extraHeaders
 */
export const makeApiProxyGatewayCall = <T = any, S = object>(
	url: string,
	method: PossibleHttpMethods,
	extraHeaders: { 'X-Jit': string; 'X-Resource-Id': string }, // must include x-Jit and X-Resource-Id here
	payload?: S,
	requestConfig?: Pick<AxiosRequestConfig, Exclude<keyof AxiosRequestConfig, 'headers'>>,
	useDefaultAxios: boolean = true
): AxiosPromise<T> => {
	const conf = {
		headers: {
			...extraHeaders,
		},
		...requestConfig,
	};

	return makeApiCall<T, S>(url, method, payload, conf, useDefaultAxios);
};

export const makeApiNonGatewayCall = <T = any, S = object>(
	url: string,
	method: PossibleHttpMethods,
	payload?: S,
	extraHeaders?: { [key: string]: string },
	requestConfig?: Pick<AxiosRequestConfig, Exclude<keyof AxiosRequestConfig, 'headers'>>,
	useDefaultAxios: boolean = true
): AxiosPromise<T> => {
	const conf = {
		headers: {
			...extraHeaders,
		},
		...requestConfig,
	};

	return makeApiCall<T, S>(url, method, payload, conf, useDefaultAxios);
};

// TODO: this is completely off, has to be fixed
export const getSubDomainToSend = (hostname?: string) => {
	const hstname = hostname || location.hostname;
	const domainArray = hstname.split('.');

	if (hstname === 'localhost') {
		domainArray[0] = 'frontendteam';
	} else if (hstname.includes('platform20')) {
		return 'frontendteam';
	} else if (process.env.NODE_ENV === 'development') {
		domainArray.pop();
		domainArray.pop();
	} else {
		domainArray.pop();
		domainArray.pop();
	}

	return domainArray.join('.');
};

export const preparePayloadObjectForUpdateCall = <T>(payload: T, ommitedFields: string[]): UpdatePayloadType<T> => {
	let newObj: any = {};

	if (typeof payload !== 'object') {
		throw new Error('argument for preparePayloadObjectForUpdateCall must be an object');
	}

	Object.keys(payload).forEach((field: Extract<keyof T, string>) => {
		const val = payload[field];

		if (ommitedFields.includes(field) || val === undefined) {
			return;
		}

		if (val === null) {
			newObj = {
				...newObj,
				[field]: {
					Value: null,
				},
			};
		} else {
			newObj = {
				...newObj,
				[field]: {
					Value: val,
				},
			};
		}
	});

	return newObj;
};

export const uploadImage = (file: File, blobUrl: string) => {
	const data: FormData = new FormData();

	data.append('0', file);

	data.append('UserId', getRepeatedDate());
	data.append('BlobUploadId', generateGuid());
	data.append('Storage', 'S3');

	return makeApiGatewayCall(blobUrl, 'post', data);
};

export const uploadImageByUrl = (imageUrl: string, blobUrl: string) => {
	const data = {
		BlobUploadId: generateGuid(),
		ExpectedContentType: 'image/',
		Storage: 'S3',
		Url: imageUrl,
		UserId: getRepeatedDate(),
	};

	return makeApiGatewayCall(blobUrl, 'post', data);
};

export const isSendUser = () => getUser()?.IsSendAccount || false;

export const getParentId = () => {
	return getUser() ? getUser().ParentUserId : null;
};
